import { Alert, Button, Checkbox, FieldBlock, Input, Modal, useToast } from '@orbind/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useBootstrap } from '../../lib/catalog.ts'
import { buscarCredenciais, buscarShare, salvarCredencial, salvarShare, type UserApp } from './data.ts'

/**
 * Compartilhar a aplicacao (so o DONO). Escolhe TODOS do workspace ou membros
 * especificos. Compartilha os DADOS + URL; usuario/senha cada um poe o seu.
 */
export function ShareDialog({ app, onClose }: { app: UserApp; onClose: () => void }) {
  const toast = useToast()
  const qc = useQueryClient()
  const bootstrap = useBootstrap()
  const [everyone, setEveryone] = useState(false)
  const [memberIds, setMemberIds] = useState<string[]>([])
  const [busca, setBusca] = useState('')
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    void buscarShare(app.id)
      .then((s) => {
        setEveryone(s.everyone)
        setMemberIds(s.memberIds)
      })
      .catch(() => {})
  }, [app.id])

  const membros = (bootstrap.data?.members ?? [])
    .filter((m) => m.isActive)
    .filter((m) => m.name.toLowerCase().includes(busca.trim().toLowerCase()))

  const alternar = (id: string) =>
    setMemberIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  const salvar = async () => {
    setSalvando(true)
    try {
      await salvarShare(app.id, { everyone, memberIds })
      await qc.invalidateQueries({ queryKey: ['apps'] })
      toast.push({ tone: 'success', message: 'Compartilhamento atualizado.' })
      onClose()
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível compartilhar.' })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Compartilhar “${app.title}”`}
      width={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" loading={salvando} onClick={() => void salvar()}>
            Salvar
          </Button>
        </>
      }
    >
      <Alert tone="info">
        Compartilha os dados e o endereço da aplicação. O <strong>usuário e a senha</strong> cada pessoa preenche o
        seu — as suas credenciais não são compartilhadas.
      </Alert>

      <div style={{ marginTop: 12 }}>
        <Checkbox
          label="Todos do workspace"
          checked={everyone}
          onChange={(e) => setEveryone(e.target.checked)}
        />
      </div>

      {!everyone ? (
        <div style={{ marginTop: 12 }}>
          <FieldBlock label="Compartilhar com pessoas">
            {({ id }) => (
              <Input id={id} placeholder="Buscar pessoas…" value={busca} onChange={(e) => setBusca(e.target.value)} />
            )}
          </FieldBlock>
          <div className="appshare__lista">
            {membros.length === 0 ? (
              <p className="appshare__vazio">Nenhuma pessoa.</p>
            ) : (
              membros.map((m) => (
                <Checkbox key={m.id} label={m.name} checked={memberIds.includes(m.id)} onChange={() => alternar(m.id)} />
              ))
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  )
}

/**
 * Credencial do RECIPIENTE numa aplicacao compartilhada: cada um poe o seu
 * usuario/senha (nao mexe nos dados, que sao do dono).
 */
export function CredencialModal({ app, onClose }: { app: UserApp; onClose: () => void }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [usuario, setUsuario] = useState('')
  const [senha, setSenha] = useState('')
  const [carregou, setCarregou] = useState(false)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    void buscarCredenciais(app.id)
      .then((c) => {
        setUsuario(c.username ?? '')
        setSenha(c.password ?? '')
      })
      .catch(() => {})
      .finally(() => setCarregou(true))
  }, [app.id])

  const salvar = async () => {
    setSalvando(true)
    try {
      await salvarCredencial(app.id, {
        loginUsername: usuario.trim() === '' ? null : usuario.trim(),
        loginPassword: senha === '' ? null : senha,
      })
      await qc.invalidateQueries({ queryKey: ['apps'] })
      toast.push({ tone: 'success', message: 'Suas credenciais foram salvas.' })
      onClose()
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível salvar.' })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Suas credenciais — ${app.title}`}
      width={460}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" loading={salvando} disabled={!carregou} onClick={() => void salvar()}>
            Salvar
          </Button>
        </>
      }
    >
      <Alert tone="info">
        Aplicação compartilhada por outra pessoa. Os dados e o endereço são do dono; aqui você guarda o{' '}
        <strong>seu</strong> usuário e senha para o preenchimento automático.
      </Alert>
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FieldBlock label="Usuário">
          {({ id }) => <Input id={id} value={usuario} onChange={(e) => setUsuario(e.target.value)} />}
        </FieldBlock>
        <FieldBlock label="Senha">
          {({ id }) => (
            <Input id={id} type="password" value={senha} onChange={(e) => setSenha(e.target.value)} />
          )}
        </FieldBlock>
      </div>
    </Modal>
  )
}
